<template>
  <modal v-model:open="internalOpen" :width="600">
    <template #header>
      <modal-header variant="cross" @cancel="closeModal()">
        <h1>{{ $t('pages.campaigns.qrModal.title') }}</h1>
      </modal-header>
    </template>

    <template #default>
      <article class="text-center mt-8">
        <div v-if="!campaignGroup?.qrToken">
          {{ $t('pages.campaigns.qrModal.notFound') }}
        </div>

        <template v-else>
          <div class="qrcode mb-6">
            <vue-qrcode
              :value="campaignGroup.qrToken"
              :options="{
                errorCorrectionLevel: 'H',
                width: 300,
              }"
              @ready="onReady"
            ></vue-qrcode>
          </div>
          <div class="flex flex-col gap-y-5">
            <custom-button variant="primary" @click="downloadQR('svg')">
              {{ $t('pages.campaigns.qrModal.downloadSVGButton') }}
            </custom-button>
            <custom-button variant="primary" @click="downloadQR('png')">
              {{ $t('pages.campaigns.qrModal.downloadPNGButton') }}
            </custom-button>
          </div>
        </template>
      </article>
    </template>
  </modal>
</template>

<script lang="ts" setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from '@components/utils/Modal/Modal.vue';
import VueQrcode from '@chenfengyuan/vue-qrcode';
import CustomButton from '@components/utils/CustomButton.vue';
import { useVModel } from '@vueuse/core';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import type { CampaignGroup } from '@/entities/Entities';
import logo from '@/assets/images/logo.png';

interface Props {
  campaignGroup?: CampaignGroup;
  open: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits(['update:open']);
const internalOpen = useVModel(props, 'open', emit);
const { t } = useI18n();

const qrCanvas = ref<HTMLCanvasElement | null>(null);
const cachedLogo = ref<HTMLImageElement | null>(null);

onMounted(() => {
  const image = new Image();
  image.src = logo;
  image.crossOrigin = 'anonymous';
  image.onload = () => {
    cachedLogo.value = image;
  };
});

function closeModal() {
  internalOpen.value = false;
}

function drawLogoWithMargin(
  context: CanvasRenderingContext2D,
  image: HTMLImageElement,
  canvasWidth: number,
  canvasHeight: number,
) {
  const totalCoverage = 0.25;
  const logoCoverage = 0.20;
  const totalWidth = Math.round(canvasWidth * totalCoverage);
  const logoWidth = Math.round(canvasWidth * logoCoverage);
  const x = (canvasWidth - totalWidth) / 2;
  const y = (canvasHeight - totalWidth) / 2;
  const logoX = x + (totalWidth - logoWidth) / 2;
  const logoY = y + (totalWidth - logoWidth) / 2;

  context.fillStyle = '#fff';
  context.fillRect(x, y, totalWidth, totalWidth);

  context.drawImage(image, logoX, logoY, logoWidth, logoWidth);
}

function onReady(canvas: HTMLCanvasElement) {
  qrCanvas.value = canvas;
  const context = canvas.getContext('2d');
  if (!context || !cachedLogo.value) return;
  drawLogoWithMargin(context, cachedLogo.value, canvas.width, canvas.height);
}

function downloadQR(extension: 'svg' | 'png') {
  if (!qrCanvas.value) return;

  const link = document.createElement('a');
  if (extension === 'png') {
    link.href = qrCanvas.value.toDataURL('image/png');
    link.download = `${props.campaignGroup?.name || 'qr-code'}.png`;
  } else {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    const image = document.createElementNS('http://www.w3.org/2000/svg', 'image');
    svg.setAttribute('width', qrCanvas.value.width.toString());
    svg.setAttribute('height', qrCanvas.value.height.toString());
    image.setAttribute('width', '100%');
    image.setAttribute('height', '100%');
    image.setAttribute('href', qrCanvas.value.toDataURL('image/png'));
    svg.appendChild(image);
    const svgData = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    link.href = URL.createObjectURL(svgBlob);
    link.download = `${props.campaignGroup?.name || 'qr-code'}.svg`;
  }
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
</script>

<style lang="scss" scoped>
.qrcode {
  display: inline-block;
  background-color: #ffffff;
}
</style>
